/**
 * Every press, accepted or refused — `09-tms-write-paths.md` §5.1.
 *
 * > *"After the press — **a persistent receipt, not just a toast.** The mockups only produce
 * > toasts, which vanish in 2.8 seconds and leave no evidence."*
 *
 * The marking screen shows the **last** attempt beside the button that made it. The profile
 * is where the whole record lives, newest first, because that is the surface a TD opens when
 * he wants to know what happened to this official's marking and report — including the
 * attempts that failed, which are exactly the ones a toast lost.
 *
 * Two rules kept verbatim from §5.1. **The receipt speaks in its own words**: `detail` is what
 * TMS or the gateway said and is rendered as it stands, never paraphrased and never softened.
 * And **`Pressed By` is who pressed, never the subject** — a mark is not necessarily entered
 * by the TD of record (§B3a item 4), and an umpire's push is the owning UM's.
 *
 * An empty list is a real state and says so: nothing has been attempted. It is not a fault,
 * and it is not dressed as one.
 */

import type { PushReceipt } from "../../api/types.js";
import { tournamentDateShort } from "../../app/tournamentTime.js";
import { pushTime } from "../marking/PushReceiptNote.js";

const KIND_LABEL: Readonly<Record<string, string>> = {
  marks: "Marks",
  report: "Report",
};

/**
 * `04 Oct 09:12` — the day matters here, unlike on the chip beside a button.
 *
 * **RUN 83 — both halves are the tournament's clock.** The time half always was, through
 * `pushTime`; the date half was `toLocaleDateString` with no `timeZone`, so on a device west of
 * the host city the two disagreed with each other inside one stamp — a push made on the evening
 * of the 4th in Nagoya was listed as `04 Oct` in Tokyo hours beside a date that said the 3rd.
 */
function receiptStamp(at: string): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return at;
  return `${tournamentDateShort(date)} ${pushTime(at)}`;
}

export function PushReceiptsPanel({
  receipts,
}: {
  receipts: readonly PushReceipt[];
}): React.JSX.Element {
  return (
    <section className="panel panel--d2" aria-labelledby="receiptsTitle" data-testid="push-receipts" data-howto="push-receipts">
      <div className="panel__head">
        <h3 className="panel__title" id="receiptsTitle">
          Push Receipts
        </h3>
        <span className="panel__note">Every Press · Accepted Or Refused</span>
      </div>

      {receipts.length === 0 ? (
        <div className="emptystate">
          <b>No Push Has Been Attempted</b>
          Nothing has been sent to TMS for this official — neither the marks nor the report.
          That is a state, not a fault: a push is a deliberate press and nobody has made one.
        </div>
      ) : (
        <table className="mtable sumtable" data-testid="receipt-table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">What</th>
              <th scope="col">Outcome</th>
              <th scope="col">Pressed By</th>
              <th scope="col">What TMS Said</th>
            </tr>
          </thead>
          <tbody>
            {receipts.map((receipt) => {
              const accepted = receipt.outcome === "accepted_draft";
              return (
                <tr key={receipt.id} data-testid={`receipt-${receipt.id}`}>
                  <td className="num">{receiptStamp(receipt.pressedAt)}</td>
                  <td>
                    {/*
                     * The internal name of the write path (`W2`, `W3`) used to print here,
                     * beside the word `Marks`, and it told a Technical Delegate nothing: it
                     * is our label for which surface a press belongs to, not a fact about
                     * his tournament. WHO pressed is the column beside this one, and it is
                     * the thing he actually wants. The code stays on the tooltip, where a
                     * support question can still reach it.
                     */}
                    <span title={`Write path ${receipt.path}`}>
                      {KIND_LABEL[receipt.kind] ?? receipt.kind}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`statuspill statuspill--${accepted ? "good" : "alert"}`}
                      title={accepted ? "Accepted by TMS as a draft" : "Nothing reached TMS"}
                    >
                      <span className="sdot" aria-hidden="true" />
                      {accepted ? "Pushed As Draft" : "Not Pushed"}
                    </span>
                  </td>
                  {/* The name, with the id on the tooltip: the table is read by a person and
                      the id is what a query would be written against.

                      The guard is FALSY and not `=== ""` on purpose. A response served by an
                      API older than this field carries no `pressedByMediaName` at all, and an
                      `undefined` slipping past an equality check renders an empty cell — which
                      is the one thing the fallback exists to prevent, since a blank `PRESSED
                      BY` reads as "nobody pressed it". Both absent and empty fall back to the
                      id, because both mean the same thing here: no name arrived. */}
                  <td title={receipt.pressedByTmsPersonId}>
                    {receipt.pressedByMediaName || receipt.pressedByTmsPersonId}
                  </td>
                  <td>{receipt.detail}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="panel__foot">
        <b>The receipt is the audit artefact —</b> it is written for both outcomes, before
        anything of ours moves, and only an accepted push advances a stage. What TMS said is
        printed as TMS said it.
      </div>
    </section>
  );
}
